
/**
 * Button component, triggers basic user interactions.
 *
 * [Online documentation](https://frontend-components.altura.io/?path=/story/controls-button--button)
 *
 * @example <altura-button type="primary" icon="send" />
 */
export default {
  props: {
    /**
     * Changes the appearance of the button component
     * @values primary, outlined, text, elevated, danger, link, gray
     */
    type: {
      type: String,
      default: 'primary',
      validator: input =>
        ['primary', 'outlined', 'text', 'elevated', 'link', 'none', 'danger'].includes(input)
    },

    /**
     * determines the width of the button component
     * @values inline, block
     */
    size: {
      type: String,
      default: 'inline',
      validator: value => ['inline', 'block'].includes(value)
    },

    /**
     * which icon to show in the button component
     * @values icons
     */
    icon: {
      /** @type {AlturaIcons} */
      type: String,
      default: undefined
    },

    /**
     * How big the icon should be in fractions
     */
    iconSize: {
      type: [Number, String],
      default: undefined
    },

    /**
     * On which side should the icon appear.
     * @values left, right
     */
    iconAlign: {
      type: String,
      validator: value => ['left', 'right'].includes(value),
      default: 'left'
    },

    /**
     * Toggles the disabled state of the button
     */
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },

    /**
     * This property is deprecated
     * @deprecated
     * @ignore
     */
    href: {
      type: String,
      default: null,
      validator () { console.warn("The 'href' prop is deprecated on the 'altura-button' component."); }
    },

    /**
     * Changes the button to a link, this is the url where the user is directed to when this button is clicked.
     */
    to: {
      type: String,
      default: undefined,
      required: false
    },

    /**
     * Only works when to is set. Changes the target to where the link should be opened.
     */
    newTab: {
      type: Boolean,
      default: false,
      required: false
    },

    /**
     * Submit the form this button is part of when clicked.
     */
    submit: {
      type: Boolean,
      default: false
    },

    /**
     * Toggles a loading state.
     */
    loading: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      /** Indicates that the button has been clicked and the user is being navigated to a new page. */
      routing: false
    };
  },

  computed: {
    classes () {
      return [
        this.type,
        `icon-${this.iconAlign}`,
        {
          'icon-only': this.iconOnly,
          'has-icon': this.icon,
          loading: this.loading || this.routing,
          block: this.size === 'block'
        }
      ];
    },

    iconCssSize () {
      if (typeof this.iconSize === 'number') {
        return this.iconSize + 'rem';
      }

      if (/^[0-9.]+$/.test(this.iconSize)) {
        return this.iconSize + 'rem';
      }

      return this.iconSize;
    },

    iconOnly () {
      return !this.$slots.default;
    }
  },

  methods: {
    openNewTab (event) {
      if ((this.disabled || !this.to) && !event) {
        this.$emit('click', event);
        return;
      }

      if (
        this.to.startsWith('http') ||
        this.to.startsWith('mailto:') ||
        this.to.startsWith('//')
      ) {
        window.open(this.to, '_blank');
        return;
      }

      const routerdata = this.$router.resolve({ path: this.to });
      window.open(routerdata.href, '_blank');
    },

    openHere () {
      this.routing = true;
      if (this.disabled || !this.to) {
        return;
      }

      if (
        this.to.startsWith('http') ||
        this.to.startsWith('mailto:') ||
        this.to.startsWith('//')
      ) {
        location.href = this.to;
        return;
      }

      this.$router.push({ path: this.to });
      setTimeout(() => (this.routing = false), 5000);
    },

    click (event) {
      if (!this.to) { return this.$emit('click', event); }

      if (this.disabled) { return; }

      if (this.newTab) {
        this.openNewTab();
      } else {
        this.openHere();
      }
    }
  }
};
